import { Accordion, Group, Text } from "@mantine/core";
import { type JSX } from "react";

import { RichPhrase } from "../../RichPhrase";
import classes from "./RichBenefitsSection.module.css";

/** What the expert sees. Deliberately not the Contentful title — see `title` below. */
const DISPLAY_LABEL = "Benefits";

type Props = {
    /**
     * The swapped-out section's Contentful title, used only as the accordion value (never displayed).
     * The previous section's completion opens its successor by matching this title, and the
     * `SalesChecklistSection_Opened` analytics event name is derived from it, so renaming the label
     * must not change it.
     */
    title: string;
    markdown: string;
};

/**
 * Rich-text replacement for a Core Benefits section. Read-only by design: it has no checklist items,
 * so there is nothing to check off, no completion badge, and `onSectionComplete` never fires for it —
 * which also means the section after it is not auto-opened.
 */
export function RichBenefitsSection({ title, markdown }: Props): JSX.Element {
    return (
        <Accordion.Item value={title}>
            <Accordion.Control styles={{ control: { opacity: 1 } }}>
                <Group justify="space-between" pr="sm">
                    <Text size="xs" c="dark.1" fw="var(--mantine-font-weight-heavy)">
                        {DISPLAY_LABEL}
                    </Text>
                </Group>
            </Accordion.Control>
            <Accordion.Panel>
                <Text component="div" size="xs" c="dark.1" classNames={{ root: classes.content }}>
                    <RichPhrase markdown={markdown} />
                </Text>
            </Accordion.Panel>
        </Accordion.Item>
    );
}
